import { memo, type PropsWithChildren } from "react";

import filters from "byzantine/src/filters";
import { Localized } from "@fluent/react";
import { dollars, entities } from "byzantine";

import { useSearchBar } from "../../context";
import styles from "../../SearchBar.module.scss";
import { canShowSectionResults } from "../../utils";

import MultiRow from "./SearchBarResults.MultiRow";
import SingleRow from "./SearchBarResults.SingleRow";

const { useAccountProductTypeName } = entities.accounts;

interface ResultsSectionProps extends PropsWithChildren {
  titleId: string;
}

const ResultsSection = memo(function ResultsSection({
  titleId,
  children,
}: ResultsSectionProps) {
  return (
    <div>
      <div className={styles["results-section-header"]}>
        <Localized id={titleId} />
      </div>
      <div className={styles["results-children"]}>{children}</div>
    </div>
  );
});

const NoMatchesSection = memo(function NoMatches(
  props: Pick<ResultsSectionProps, "titleId">
) {
  return (
    <ResultsSection {...props}>
      <Localized id="search-bar-results-no-matches" />
    </ResultsSection>
  );
});

export const PagesResults = memo(function PagesResults() {
  const { resultsFilter, pagesResults } = useSearchBar();

  if (resultsFilter === "pages" && !pagesResults.length) {
    return (
      <NoMatchesSection titleId={"search-bar-results-section-title-pages"} />
    );
  }

  if (canShowSectionResults("pages", resultsFilter, pagesResults)) {
    return (
      <ResultsSection titleId={"search-bar-results-section-title-pages"}>
        {pagesResults.map(({ name, score, url }) => (
          <SingleRow
            key={`${name}--${score}`}
            title={name}
            iconRight="chevron-right"
            route={url}
          />
        ))}
      </ResultsSection>
    );
  }

  return null;
});

export const TransactionsResults = memo(function TransactionsResults() {
  const { resultsFilter, transactionsResults } = useSearchBar();

  if (resultsFilter === "transactions" && !transactionsResults.length) {
    return (
      <NoMatchesSection
        titleId={"search-bar-results-section-title-transactions"}
      />
    );
  }

  if (
    canShowSectionResults("transactions", resultsFilter, transactionsResults)
  ) {
    return (
      <ResultsSection titleId={"search-bar-results-section-title-transactions"}>
        {transactionsResults.map(
          ({ id, description, amount, datetime, account: accountId }) => (
            <MultiRow
              key={id}
              title={description}
              subtitle={filters.longMonthDayYear(datetime)}
              rightItem={filters.currency(dollars(amount), {
                hasSign: true,
                hasDecimal: true,
              })}
              route={`/accounts/${accountId}?transaction=${id}`}
            />
          )
        )}
      </ResultsSection>
    );
  }

  return null;
});

export const AccountsResults = memo(function AccountsResults() {
  const { accountSearchResults, resultsFilter } = useSearchBar();
  const getProductType = useAccountProductTypeName();

  if (resultsFilter === "accounts" && !accountSearchResults.length) {
    return (
      <NoMatchesSection titleId={"search-bar-results-section-title-accounts"} />
    );
  }

  if (canShowSectionResults("accounts", resultsFilter, accountSearchResults)) {
    return (
      <ResultsSection titleId={"search-bar-results-section-title-accounts"}>
        {accountSearchResults.map(
          ({ name, balances, product_type, source, id: uuid }) => {
            const { available: balance } = balances;
            const route = source === "institution" ? `/accounts/${uuid}` : "#";
            const productType = getProductType(product_type);
            return (
              <MultiRow
                key={uuid}
                title={name}
                subtitle={productType}
                rightItem={balance}
                route={route}
              />
            );
          }
        )}
      </ResultsSection>
    );
  }

  return null;
});
